import React, { useState } from 'react';
import { Input, Button, Modal, Spin } from 'antd';
import { useLaborInsForm } from './hooks/useLaborInsForm';

// Number input that won't lose focus during typing, with max validation
const NumInput = ({ value, onChange, disabled, width = 130, max = 9999999999 }: {
  value: number; onChange: (v: number) => void; disabled: boolean; width?: number; max?: number;
}) => {
  const [text, setText] = useState(String(value));
  React.useEffect(() => { setText(String(value)); }, [value]);
  return (
    <Input
      value={text}
      onChange={e => setText(e.target.value)}
      onBlur={() => {
        const n = parseFloat(text);
        const val = isNaN(n) ? 0 : n;
        if (val > max) {
          import('antd').then(({ message }) => message.warning(`數值不可超過 ${max.toLocaleString()}`));
          onChange(max);
          setText(String(max));
        } else {
          onChange(val);
        }
      }}
      disabled={disabled}
      style={{ width, textAlign: 'right' }}
    />
  );
};

// Field must be defined OUTSIDE the component to avoid re-mount on every render
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div><label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>{children}</div>
);

const P9136Form: React.FC = () => {
  const f = useLaborInsForm();
  const [searchText, setSearchText] = useState('');

  const handleDelete = () => {
    if (!f.current) return;
    Modal.confirm({
      title: '確認刪除', content: `確定要刪除投保薪資「${f.current.f936_01}」？`,
      okText: '刪除', okType: 'danger', cancelText: '取消', onOk: f.doDelete,
    });
  };

  const modeLabel = f.mode === 'add' ? '新增' : f.mode === 'edit' ? '修改' : '瀏覽';

  return (
    <Spin spinning={f.loading}>
      <div className="max-w-3xl mx-auto p-6">
        <h2 className="text-xl font-bold mb-4">
          勞保參數建檔 (pw9136f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {f.records.length > 0 ? `${f.currentIdx + 1} / ${f.records.length}` : '無資料'}
          </span>
        </h2>

        <div className="flex items-center gap-2 mb-3">
          <Input placeholder="輸入薪資查詢" value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={() => searchText.trim() && f.quickSearch(searchText.trim())}
            disabled={f.isEditing} style={{ width: 200 }} />
          <Button onClick={() => searchText.trim() && f.quickSearch(searchText.trim())} disabled={f.isEditing}>查詢</Button>
          <span className="mx-2" />
          <Button size="small" onClick={f.navFirst} disabled={f.isEditing || f.records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={f.navPrev} disabled={f.isEditing || f.currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={f.navNext} disabled={f.isEditing || f.currentIdx >= f.records.length - 1}>&gt;</Button>
          <Button size="small" onClick={f.navLast} disabled={f.isEditing || f.records.length === 0}>&gt;|</Button>
        </div>

        <div className="space-y-3 mb-6">
          <div className="flex gap-4">
            <Field label="月投保薪資 (F936_01)">
              <NumInput value={f.salary} onChange={f.setSalary}
                disabled={f.mode !== 'add'} width={150} max={999999} />
            </Field>
          </div>

          <div className="flex gap-4">
            <Field label="個人每日勞保費 (F936_12)">
              <NumInput value={f.dailyFee} onChange={f.setDailyFee}
                disabled={!f.isEditing} width={150} max={999999999.9} />
            </Field>
            <Field label="個人每月勞保費 (F936_13)">
              <NumInput value={f.monthlyFee} onChange={f.setMonthlyFee}
                disabled={!f.isEditing} width={150} max={9999999999} />
            </Field>
          </div>
        </div>

        {f.current && f.mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-4">
            建立: {f.current.f936_91} | 日期: {f.current.f936_92 || '-'} | 修改: {f.current.f936_93}
          </div>
        )}

        <div className="flex items-center gap-2">
          {!f.isEditing ? (
            <>
              <Button type="primary" onClick={f.startAdd}>新增</Button>
              <Button onClick={f.startEdit} disabled={!f.current}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!f.current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={f.save}>儲存</Button>
              <Button onClick={f.cancel}>取消</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default P9136Form;
